import { API_BASE_URL } from '@/config';
import { Loader2, RefreshCw, Timer } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

interface TVWorkout {
  id: string;
  name: string;
  description: string | null;
  movements: string[];
  scoring: {
    type: string;
    direction: string;
    instructions: string;
  };
  equipment: string[];
  scheduled_date: string;
  track_name: string;
  refresh_interval_seconds: number;
}

export default function TVDisplay() {
  const { workoutId } = useParams<{ workoutId: string }>();
  const [workout, setWorkout] = useState<TVWorkout | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchWorkout = useCallback(async () => {
    if (!workoutId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/public/tv/${workoutId}`);
      const data = await response.json();

      if (data.success && data.data) {
        setWorkout(data.data);
        setError(null);
      } else {
        setError(data.error || 'Workout not found');
      }
    } catch (err) {
      console.error('Failed to fetch workout:', err);
      setError('Failed to load workout');
    } finally {
      setIsLoading(false);
      setLastRefresh(new Date());
    }
  }, [workoutId]);

  useEffect(() => {
    fetchWorkout();
  }, [fetchWorkout]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(fetchWorkout, 300000);
    return () => clearInterval(interval);
  }, [fetchWorkout]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#FFBB38]" />
      </div>
    );
  }

  if (error || !workout) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white">
        <div className="text-center">
          <img
            src="/ergshoot-logo.png"
            alt="ErgShoot"
            className="h-16 w-auto mx-auto mb-4 opacity-50"
          />
          <h1 className="text-2xl font-bold mb-2">Workout Not Found</h1>
          <p className="text-gray-400">{error || 'The requested workout does not exist.'}</p>
        </div>
      </div>
    );
  }

  const scoringTypeLabel =
    workout.scoring.type === 'time'
      ? 'For Time'
      : workout.scoring.type === 'reps'
        ? 'For Reps'
        : workout.scoring.type === 'rounds'
          ? 'Rounds + Reps'
          : 'Points';

  const isStandardsOrSentinel =
    workout.scheduled_date === '9999-12-31' || workout.track_name === 'Standards';
  const dateFormatted = isStandardsOrSentinel
    ? null
    : new Date(workout.scheduled_date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-8 lg:p-12 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-12 w-auto" />
          <div>
            <h1 className="text-lg font-medium text-gray-400">ErgShoot</h1>
            <p className="text-sm text-gray-500">{dateFormatted || workout.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <RefreshCw className="h-4 w-4" />
          <span>
            Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center max-w-5xl mx-auto w-full">
        {/* Track Badge */}
        <div className="mb-4">
          <span className="inline-block px-4 py-1.5 bg-[#FFBB38]/10 text-[#FFBB38] rounded-full text-lg font-medium border border-[#FFBB38]/20">
            {workout.track_name}
          </span>
        </div>

        {/* Workout Name */}
        <h2 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">{workout.name}</h2>

        {/* Scoring Type */}
        <div className="flex items-center gap-3 mb-8">
          <Timer className="h-6 w-6 text-[#FFBB38]" />
          <span className="text-2xl lg:text-3xl font-semibold text-[#FFBB38]">
            {scoringTypeLabel}
          </span>
          <span className="text-lg text-gray-400 ml-2">
            ({workout.scoring.direction === 'ascending' ? 'Lower is better' : 'Higher is better'})
          </span>
        </div>

        {/* Description */}
        {workout.description && (
          <div className="mb-8 p-6 rounded-xl bg-white/5 border border-white/10">
            <p className="text-xl lg:text-2xl leading-relaxed whitespace-pre-wrap text-gray-200">
              {workout.description}
            </p>
          </div>
        )}

        {/* Workout */}
        {workout.movements.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-400 mb-4 uppercase tracking-wider">
              Workout
            </h3>
            <pre className="whitespace-pre-wrap font-mono text-xl lg:text-2xl text-gray-200 bg-white/5 border border-white/10 rounded-xl p-6">
              {workout.movements.join('\n')}
            </pre>
          </div>
        )}

        {/* Equipment */}
        {workout.equipment.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-400 mb-3 uppercase tracking-wider">
              Equipment
            </h3>
            <div className="flex flex-wrap gap-3">
              {workout.equipment.map((item) => (
                <span
                  key={item}
                  className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-lg"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Scoring Instructions */}
        <div className="mt-8 p-4 rounded-lg bg-[#FFBB38]/5 border border-[#FFBB38]/20">
          <p className="text-lg text-[#FFBB38]">{workout.scoring.instructions}</p>
        </div>
      </div>
    </div>
  );
}
